import {
  Client,
  ClientRepresentationStatusEnum,
  EntityTypeEnum,
  PermissionEnum,
  WorkspaceTypeEnum,
} from '@sr-sdks/api-sdk-axios';
import { Alert, Icon } from '@sr/ui-library';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { fetchClientsPaginated } from '../../api/workspaces/clientApi';
import AppMultiStepsModal from '../../components/AppMultiStepsModal';
import {
  ConfirmClients as ConfirmClientsComponent,
  UNCONFIRMED_CLIENTS_FETCH_LIMIT,
} from '../../components/ConfirmClients';
import IfFeatureEnabled from '../../components/IfFeatureEnabled';
import { AppLoader } from '../../components/Loader';
import { ReviewClients as ReviewClientsComponent } from '../../components/ReviewClients';
import { STAFF_ROUTES } from '../../constant/routes';
import { userService } from '../../reducers/permissions/userSlice';
import { reduxServices } from '../../reducers/reduxServices';
import { useAppDispatch, useTypedSelector } from '../../stateStore';
import { AuthHelper } from '../../utils/auth/authHelper';
import { isLoadingState } from '../../utils/helpers/common';
import { AlertConfirmClients } from './Alerts/AlertConfirmClients';
import { AlertOnboarding } from './Alerts/AlertOnboarding';
import { AlertTermsConditions } from './Alerts/AlertTermsConditions';
import AlertsForUnverifiedBusinesses from './Alerts/AlertsForUnverifiedBusinesses';
import UnverifiedBusinessAlert from './Alerts/UnverifiedBusinessAlert';
import { CompletedOnboardingStep } from './ConfirmClientsSteps/CompletedOnboardingStep';
import GetStartedOnboardingStep from './ConfirmClientsSteps/GetStartedOnboardingStep';
import { WorkspaceSelector } from './WorkspaceSelector';

type ClientWorkspaceKeys =
  'workspaceClients' | 'workspaceId' | 'workspaceName' | 'workspaceType';

type ClientModalProps = {
  clients: Client[];
  isClientModalOpen: boolean;
  workspaceId: string;
};

type ClientListProps = {
  clients: Client[];
};

interface IAcceptTermsProps {
  alertMessage: string;
}

const OldLayout = () => {
  const [unconfirmedClientsByWorkspace, setUnconfirmedClientsByWorkspace] =
    useState<Record<ClientWorkspaceKeys, Client[] | string | undefined>[]>([]);

  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [updateClients, setUpdateClients] = useState<boolean>(false);
  // Mirrors AlertConfirmClients' `fetching` flag: the old Redux-backed
  // `isClientLoading` selector gated the page until the per-workspace client
  // fetch finished. Without an equivalent, the "Confirm Now" alert pops in
  // (and shifts layout) once `getAllWorkspaceClients` finishes after first
  // paint, instead of the page waiting for it like before.
  //
  // First load only: a refetch after the modal closes keeps the page (and the
  // other workspaces' banners) on screen, as the Redux flag did once any one
  // workspace's fetch had settled (APP-2334).
  const [isClientsFetching, setIsClientsFetching] = useState<boolean>(false);
  const [hasFetchedClients, setHasFetchedClients] = useState<boolean>(false);
  const [showClientModal, setShowClientModal] = useState<ClientModalProps>({
    clients: [],
    isClientModalOpen: false,
    workspaceId: '',
  });

  const [clientList, setClientList] = useState<ClientListProps>({
    clients: [],
  });

  const [isSavingClients, setIsSavingClients] = useState(false);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = location;

  const currentUser = useTypedSelector(userService.selectors.userMe);

  const workspaces = useTypedSelector(
    reduxServices.workspace.selectors.workspaces,
  );

  const isWorkspaceLoading = useTypedSelector(
    reduxServices.workspace.selectors.isLoading,
  );

  const userMe = useTypedSelector(userService.selectors.userMe);

  const unverifiedBusinesses = workspaces?.filter(
    (workspace) =>
      !workspace.businessNumberVerified &&
      workspace.businessNumberCountry === 'AU' &&
      workspace.entityType === EntityTypeEnum.Company,
  );

  // Workspace owner and admin will see the unverified business alert
  const shouldShowUnverifiedBusinessAlert = useMemo(() => {
    if (!userMe) return false;

    const auth = new AuthHelper(userMe);

    return (
      auth.currentUserHasPermission(PermissionEnum.EditWorkspaceDetails) &&
      unverifiedBusinesses.length > 0
    );
  }, [unverifiedBusinesses.length, userMe]);

  const handleClose = () => {
    setShowAlert(false);
    history.replaceState({}, 'undefined');
  };

  const getAllWorkspaceClients = useCallback(async () => {
    if (!currentUser) return;
    setIsClientsFetching(true);

    try {
      const workspaceIds = AuthHelper.forUser(currentUser).getWorkspaceIds(
        PermissionEnum.VerifyClientRepresentation,
      );

      const clientByWorkspacePromises = workspaceIds.map(
        async (workspaceId: string | undefined) => {
          const workspaceClients = await fetchClientsPaginated({
            pageSize: UNCONFIRMED_CLIENTS_FETCH_LIMIT,
            representationStatus: ClientRepresentationStatusEnum.Unconfirmed,
            workspaceId: workspaceId ?? '',
          });

          return {
            workspaceClients: workspaceClients.items,
            workspaceId: workspaceId,
            workspaceName: workspaces.find(
              (workspace) => workspace._id === workspaceId,
            )?.workspaceName,
            workspaceType: workspaces.find(
              (workspace) => workspace._id === workspaceId,
            )?.workspaceType,
          };
        },
      );

      const clients = await Promise.all(clientByWorkspacePromises);

      setUnconfirmedClientsByWorkspace(
        clients.filter(
          (client: {
            workspaceClients: any[] | string;
            workspaceType: string | undefined;
          }) =>
            client.workspaceClients.length > 0 &&
            client.workspaceType === WorkspaceTypeEnum.BusinessAgency,
        ),
      );
    } finally {
      setIsClientsFetching(false);
      setHasFetchedClients(true);
    }
  }, [currentUser, workspaces]);

  const handleUpdatedClients = (clients: Client[]) => {
    setClientList({
      clients: clients,
    });
  };

  const handleCloseModal = () => {
    // Reset all
    setShowClientModal({
      clients: [],
      isClientModalOpen: false,
      workspaceId: '',
    });
    // Otherwise the next workspace's modal opens on this one's clients.
    setClientList({ clients: [] });

    setUpdateClients(false);
  };

  useEffect(() => {
    if (!currentUser) return;
    const auth = new AuthHelper(currentUser);

    // Redirect if allowed
    if (auth.currentUserHasPermission(PermissionEnum.ViewStaffHomepage)) {
      navigate(STAFF_ROUTES.dashboard, { replace: true });

      return;
    }

    // The modal can't close mid-save, so this also holds the refetch until
    // the saves settle.
    if (showClientModal.isClientModalOpen) return;

    dispatch(reduxServices.workspace.findAll());
    getAllWorkspaceClients();
    setUpdateClients(true);
  }, [
    currentUser,
    dispatch,
    getAllWorkspaceClients,
    navigate,
    showClientModal.isClientModalOpen,
    state,
    updateClients,
  ]);

  useEffect(() => {
    if (state?.from === 'onboarding') {
      setShowAlert(true);
      window.scrollTo(0, 0);
    }

    return () => {
      if (!location.pathname.includes('dashboard')) {
        history.replaceState({}, 'undefined');
      }
    };
  }, [state?.from, location.pathname]);

  if (
    isLoadingState(isWorkspaceLoading) ||
    (isClientsFetching && !hasFetchedClients)
  ) {
    return <AppLoader.FlexLoader />;
  }

  const workpaceUrl = workspaces[0]?._id
    ? '/workspace/' + workspaces[0]?._id
    : '/dashboard';

  const handleAcknowledgeClick = async () => {
    await dispatch(
      userService.updateMe({
        firstName: currentUser?.firstName as string,
        lastName: currentUser?.lastName as string,
        privacyPolicyDate: new Date().toISOString(),
      }),
    );

    return dispatch(userService.findMe());
  };

  const AcceptTermsSection = ({ alertMessage }: IAcceptTermsProps) => {
    if (!currentUser?.privacyPolicyDate)
      return (
        <div className="flex h-auto w-full px-5 xl:mx-auto xl:max-w-6xl">
          <div className="flex w-full">
            <Alert
              actionButtonProps={{
                children: 'Acknowledge',
                onClick: handleAcknowledgeClick,
              }}
              className="mb-5"
              hasActionButton
              variant="info"
            >
              <span dangerouslySetInnerHTML={{ __html: alertMessage }} />
            </Alert>
          </div>
        </div>
      );

    return null;
  };

  return (
    <>
      <Helmet>
        <title>Dashboard | Screenrights</title>
      </Helmet>
      <AcceptTermsSection alertMessage="We've moved offices so Screenrights' Privacy Policy has been updated to show our new address. You can review the Privacy Policy <a target='_blank' href='https://www.screenrights.org/privacy-policy'><strong>here</strong></a>. By clicking ‘Acknowledge’, you confirm that you accept this change." />

      {showAlert && (
        <div className="flex h-auto w-full px-5 xl:mx-auto xl:max-w-6xl">
          <div className="flex w-full">
            <Alert
              title={state?.withBankDetails ? 'Onboarding successful!' : ''}
              actionButtonProps={{
                children: 'Acknowledge',
                onClick: handleAcknowledgeClick,
              }}
              className="mb-5"
              variant={state?.withBankDetails ? 'success' : 'warning'}
            >
              {state?.withBankDetails
                ? 'Now, sit back and relax. We will notify you via email as soon as you receive a payment!'
                : 'Looks like you have skipped adding your bank account details. To avoid delays in payment, please add it as soon as you can.'}
              <Icon
                className="absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 drop-shadow-lg"
                icon="x"
                onClick={handleClose}
              />
            </Alert>
          </div>
        </div>
      )}
      {unconfirmedClientsByWorkspace.map((client) => (
        <div
          key={String(client.workspaceId)}
          className="flex h-auto w-full px-5 xl:mx-auto xl:max-w-6xl"
        >
          <Alert
            actionButtonProps={{
              children: 'Confirm Now',
              onClick: () => {
                setShowClientModal({
                  clients: client.workspaceClients as Client[],
                  isClientModalOpen: true,
                  workspaceId: String(client.workspaceId),
                });
              },
            }}
            className="mb-5"
            hasActionButton
            variant="info"
          >
            <p>
              Screenrights needs all agents to confirm the representation status
              of their clients. We have detected one or more clients are
              unconfirmed on your workspace -{' '}
              <strong>{String(client.workspaceName)}</strong>. Click
              &apos;Confirm Now&apos; to start the confirmation process.
            </p>
          </Alert>
        </div>
      ))}
      {shouldShowUnverifiedBusinessAlert &&
        unverifiedBusinesses.map((business, index) => (
          <UnverifiedBusinessAlert key={index} unverifiedBusiness={business} />
        ))}
      <div className="flex flex-col place-content-center items-center gap-8 px-5 md:flex-row md:gap-32">
        <div className="flex w-[385px] flex-col gap-10 px-5 text-center sm:items-start sm:px-0 sm:text-start">
          <h1 className="text-5xl leading-normal text-sr-black">
            Welcome to Screenrights
          </h1>
          <p className="w-[359px] font-sans text-2xl font-normal leading-normal text-read-only-text">
            All that we do supports learning and creativity
          </p>
          <Link
            className="btn btn-primary w-full rounded-2xl text-base leading-normal text-neutral"
            to={workpaceUrl}
          >
            Go to Workspace
          </Link>
        </div>
        <div className="flex flex-col items-start gap-10">
          <div className="mx-auto h-full max-h-[664px] w-full">
            <div className="relative w-full">
              <img
                alt="Image: Babyteeth, Whitefalk Films. Photo by: Lisa Tomasetti"
                className="aspect-square w-full xl:max-w-none"
                src="/image-Babyteeth-Whitefalk-Films.png"
              />
              <img
                alt="Screenrights"
                className="absolute bottom-0 right-0 mb-5 mr-5 w-40"
                src="/screenrights-logo.png"
              />
            </div>
            <div className="relative mt-2">
              <p className="absolute inset-y-0 left-0 text-[8px] italic sm:text-xs">
                Image: Babyteeth,{' '}
                <strong className="font-bold not-italic text-read-only-text">
                  Whitefalk Films
                </strong>
              </p>
              <p className="absolute inset-y-0 right-0 text-[8px] sm:text-xs">
                Photo By: Lisa Tomasetti
              </p>
            </div>
          </div>
        </div>
      </div>
      <AppMultiStepsModal
        isOpen={showClientModal.isClientModalOpen}
        steps={[
          {
            classes: 'intro',
            component: <GetStartedOnboardingStep />,
            nextButtonText: 'Continue',
            showPrevious: false,
            title: 'Get Started',
          },
          {
            classes: 'steps confirm-clients',
            component: (
              <ConfirmClientsComponent
                className="pt-14"
                clientWorkspaceId={String(showClientModal.workspaceId)}
                unconfirmedClients={
                  clientList.clients.length
                    ? clientList.clients
                    : showClientModal.clients
                }
                onUpdatedClients={handleUpdatedClients}
              />
            ),
            nextButtonText: 'Proceed to Review',
            showPrevious: false,
            title: 'Confirm Clients',
          },
          {
            classes: 'steps review-clients',
            component: (
              <ReviewClientsComponent
                clientWorkspaceId={String(showClientModal.workspaceId)}
                clientsList={
                  clientList.clients.length
                    ? clientList.clients
                    : showClientModal.clients
                }
              />
            ),
            nextButtonText: 'Save & Continue',
            showPrevious: true,
            title: 'Review Clients',
          },
          {
            classes: 'steps done',
            component: (
              <CompletedOnboardingStep
                clientsList={
                  clientList.clients.length
                    ? clientList.clients
                    : showClientModal.clients
                }
                onSavingChange={setIsSavingClients}
              />
            ),
            isLoading: isSavingClients,
            nextButtonText: 'Close',
            showPrevious: false,
            title: 'Completed',
          },
        ]}
        onCloseClick={handleCloseModal}
      />
    </>
  );
};

const Dashboard = () => (
  <>
    <Helmet>
      <title>Dashboard | Screenrights</title>
    </Helmet>

    <IfFeatureEnabled name="whatsNew" isEnabled="true">
      <div className="container md:p-0">
        <div className="mb-12 mt-8">
          <h1 className="mb-6 text-5xl">Welcome to Screenrights</h1>

          <WorkspaceSelector />
        </div>

        <div className="flex gap-9 max-lg:flex-col [&>*]:basis-1/2">
          <div className="space-y-4 lg:order-last">
            <h2 className="mb-6 text-sr-black">Alerts</h2>

            <AlertTermsConditions className="xl:mx-auto xl:max-w-6xl" />

            <AlertOnboarding className="xl:mx-auto xl:max-w-6xl" />

            <AlertsForUnverifiedBusinesses />

            <AlertConfirmClients />
          </div>

          <div className="space-y-4">
            <h2 className="mb-6 text-sr-black">What&apos;s New</h2>
          </div>
        </div>
      </div>
    </IfFeatureEnabled>

    <IfFeatureEnabled name="whatsNew" isEnabled="false">
      <OldLayout />
    </IfFeatureEnabled>
  </>
);

export default Dashboard;
